import React from 'react'

import { useTranslation } from '@/features/locale/LocaleProvider'
import { LiquidityMobileTokenPickerSheet } from '@/mobile/token-picker/LiquidityMobileTokenPickerSheet'
import { MintField as Field } from '@/domain/liquidity'
import type { AddLiquidityControllerResult } from '@/features/liquidity/useAddLiquidityController'
import { useAppWeb3 } from '@/hooks'
import { ChainId } from '@/sdk'

import {
  LiquidityNutrimaticMobileAddFormView,
  LiquidityNutrimaticMobileAddReviewView,
  LiquidityNutrimaticMobileAddSuccessView,
  LiquidityNutrimaticMobileAddCancelledView,
  LiquidityNutrimaticMobileAddErrorView,
} from '../mobile/LiquidityNutrimaticMobileAddView'
import { useLiquidityAddNutrimaticFlow } from '../useLiquidityAddNutrimaticFlow'

/** Panel derecho de la tableta para añadir liquidez (form → review → success). */
export function LiquidityNutrimaticAddPerformStack({
  controller,
}: {
  controller: AddLiquidityControllerResult
}) {
  const { chainId } = useAppWeb3()
  const t = useTranslation()
  const flow = useLiquidityAddNutrimaticFlow(controller)
  const { screen, tokenSearch } = flow

  const onTokenPick = (field: 'A' | 'B', currency: Parameters<typeof controller.handleCurrencyASelect>[0]) => {
    if (field === 'A') controller.handleCurrencyASelect(currency)
    else controller.handleCurrencyBSelect(currency)
  }

  return (
    <>
      <div className="nm-tablet-trade__stage-stack" data-nutrimatic-mobile-simple>
        <div
          className="nm-mobile-simple__stage nm-mobile-simple__convert-layer flex min-h-0 flex-col overflow-hidden"
          hidden={screen !== 'form'}
          inert={screen !== 'form' ? true : tokenSearch.open ? true : undefined}
          aria-hidden={screen !== 'form' ? true : tokenSearch.open ? true : undefined}
        >
          <LiquidityNutrimaticMobileAddFormView
            controller={controller}
            tokenSearch={tokenSearch}
            variant="desktop-tablet"
          />
        </div>

        <div
          className="nm-mobile-simple__stage nm-mobile-simple__review-stage flex min-h-0 flex-col overflow-hidden"
          hidden={screen !== 'review'}
          aria-hidden={screen !== 'review'}
        >
          <LiquidityNutrimaticMobileAddReviewView controller={controller} variant="desktop-tablet" />
        </div>

        <div
          className="nm-mobile-simple__stage nm-mobile-simple__success-stage flex min-h-0 flex-col overflow-hidden"
          hidden={screen !== 'success'}
          aria-hidden={screen !== 'success'}
        >
          <LiquidityNutrimaticMobileAddSuccessView controller={controller} />
        </div>

        <div
          className="nm-mobile-simple__stage nm-mobile-simple__outcome-stage flex min-h-0 flex-col overflow-hidden"
          hidden={screen !== 'cancelled'}
          aria-hidden={screen !== 'cancelled'}
        >
          <LiquidityNutrimaticMobileAddCancelledView controller={controller} />
        </div>

        <div
          className="nm-mobile-simple__stage nm-mobile-simple__outcome-stage flex min-h-0 flex-col overflow-hidden"
          hidden={screen !== 'error'}
          aria-hidden={screen !== 'error'}
        >
          <LiquidityNutrimaticMobileAddErrorView controller={controller} />
        </div>
      </div>

      {screen === 'form' ? (
        <LiquidityMobileTokenPickerSheet
          open={tokenSearch.open}
          onClose={tokenSearch.close}
          placement="embedded"
          chainId={chainId ?? ChainId.INFINITE_CREATIVE}
          title={tokenSearch.activeField === 'A' ? t.common.selectTokenA : t.common.selectTokenB}
          excludeCurrency={
            tokenSearch.activeField === 'A'
              ? (controller.currencies[Field.CURRENCY_B] ?? undefined)
              : (controller.currencies[Field.CURRENCY_A] ?? undefined)
          }
          onSelect={(currency) => tokenSearch.selectCurrency(currency, onTokenPick)}
        />
      ) : null}
    </>
  )
}
